import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { IdNameDto, ParkingSubscriptionListRow } from '../../../../../core/models/finance/master-fee.model';
import { MapperMasterFeeService } from '../../../../../core/mappers/finance/mapper-master-fee.service';
import { ApiMasterFeeService } from '../../../../../core/services/api/finance/api-master-fee.service';
import { ApiParkingCardListService } from '../../../../../core/services/api/parking/api-parking-card-list.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { SiteStateService } from '../../../../../core/services/site-state.service';
import { ModalAdminActivityHistoryComponent } from '../../system-settings/modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../../core/models/system-settings/admin-activity-log.model';
import { ParkingVehicleTypeResponse } from '../../../../../core/models/vehicle.model';

@Component({
  selector: 'app-modal-parking-subscription',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalAdminActivityHistoryComponent],
  templateUrl: './modal-parking-subscription.component.html',
})
export class ModalParkingSubscriptionComponent implements OnInit {
  @Input() editRow: ParkingSubscriptionListRow | null = null;
  @Input() siteId = 0;
  @Input() siteName = '';
  @Output() close = new EventEmitter<void>();
  @Output() saveSuccess = new EventEmitter<void>();

  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;
  formData!: ParkingSubscriptionListRow;
  fees: IdNameDto[] = [];
  rates: IdNameDto[] = [];
  gateGroups: IdNameDto[] = [];
  vehicleTypes: IdNameDto[] = [];
  isSaving = signal(false);

  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiMasterFeeService);
  private readonly apiParkingCard = inject(ApiParkingCardListService);
  private readonly mapper = inject(MapperMasterFeeService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  get isEdit(): boolean {
    return (this.editRow?.id ?? 0) > 0;
  }

  ngOnInit(): void {
    this.formData = this.editRow
      ? { ...this.editRow }
      : this.mapper.createEmptySubscription(this.siteId);
    this.formData.siteId = this.siteId;
    this.loadOptions();
  }

  onClose(): void {
    this.close.emit();
  }

  onSave(): void {
    if (!this.siteId) {
      this.toast.error('กรุณาเลือกลานจาก Sidebar');
      return;
    }
    if (!this.formData.name?.trim()) {
      this.toast.error('กรุณาระบุชื่อ Subscription');
      return;
    }
    if (this.formData.isFreeParking && !(this.formData.vehicleTypeId && this.formData.vehicleTypeId > 0)) {
      this.toast.error('Subscription จอดฟรีต้องเลือกประเภทรถ');
      return;
    }

    const isEdit = this.isEdit;
    if (isEdit && !this.permissionService.canUpdate(this.router.url)) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    if (!isEdit && !this.permissionService.canCreate(this.router.url)) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }

    const userId = this.resolveUserId();
    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู');
      return;
    }

    const payload = this.mapper.mapSubscriptionPayload(this.formData, userId, menuId, this.siteId);
    const request$ = isEdit
      ? this.api.updateParkingSubscription(this.formData.id, payload)
      : this.api.insertParkingSubscription(payload);

    this.isSaving.set(true);
    request$.subscribe({
      next: (res) => {
        const mapped = this.mapper.mapActionResponse(res);
        this.isSaving.set(false);
        if (mapped.isSuccess) {
          this.toast.success(mapped.message || 'บันทึกสำเร็จ');
          this.saveSuccess.emit();
          this.onClose();
        } else {
          this.toast.error(mapped.message || 'บันทึกไม่สำเร็จ');
        }
      },
      error: (err) => {
        this.isSaving.set(false);
        this.toast.error(err?.error?.message || 'เกิดข้อผิดพลาด');
      },
    });
  }

  private loadOptions(): void {
    this.api.getParkingFeeDropdown().subscribe({
      next: (res) => (this.fees = this.mapper.mapIdNameList(res)),
      error: () => (this.fees = []),
    });

    this.apiParkingCard.getParkingVehicleTypes().subscribe({
      next: (res) => {
        this.vehicleTypes = (res ?? [])
          .map((v) => {
            const raw = v as ParkingVehicleTypeResponse & Record<string, unknown>;
            return {
              id: Number(raw.id ?? raw['Id'] ?? 0),
              name: String(raw.typeName ?? raw['TypeName'] ?? raw['name'] ?? raw['Name'] ?? ''),
            };
          })
          .filter((v) => v.id > 0);
      },
      error: () => (this.vehicleTypes = []),
    });

    if (!this.siteId) return;

    this.api.getParkingRateBySite(this.siteId).subscribe({
      next: (res) => (this.rates = this.mapper.mapRateOptions(res)),
      error: () => (this.rates = []),
    });

    this.api.getParkingGateGroupDropdown(this.siteId).subscribe({
      next: (res) => (this.gateGroups = this.mapper.mapIdNameList(res)),
      error: () => (this.gateGroups = []),
    });
  }

  private resolveUserId(): number {
    const fromLocal = localStorage.getItem('userId');
    if (fromLocal) return Number(fromLocal);
    return Number(this.cookieService.get('user_id')) || 0;
  }

  get canShowActivityHistory(): boolean {
    return (this.editRow?.id ?? 0) > 0;
  }

  openActivityHistory(): void {
    if (!this.canShowActivityHistory) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }
}
